import { createContext, useContext, useMemo } from 'react';

import type { ISuggestionAnnotation } from './annotationCommands/SuggestionAnnotation';

type AnnotationActionCallback = (annotation: ISuggestionAnnotation) => void;
type AnnotationActionCallbackWithLoading = {
  fn: AnnotationActionCallback;
  isLoading: boolean;
};

export type GeometricAnnotationFlyoutActions = {
  setSizeX: AnnotationActionCallbackWithLoading;
  setSizeY: AnnotationActionCallbackWithLoading;
  setSizeZ: AnnotationActionCallbackWithLoading;
  getEqualDimensions: (annotation: ISuggestionAnnotation) => string[];
};

export type PartDescriptorAnnotationFlyoutActions = {
  setPartNumber?: AnnotationActionCallbackWithLoading | null;
  setDescription?: AnnotationActionCallbackWithLoading | null;
  setRevision?: AnnotationActionCallbackWithLoading | null;
};

export type AnnotationFlyoutActions = {
  geometric: GeometricAnnotationFlyoutActions | null;
  partDescriptor: PartDescriptorAnnotationFlyoutActions | null;
};

type GetAnnotationFlyoutActions = (
  annotation: ISuggestionAnnotation,
) => AnnotationFlyoutActions;

type AnnotationFlyoutActionContextType = {
  getActions: GetAnnotationFlyoutActions;
};

const DEFAULT_VALUE: AnnotationFlyoutActionContextType = {
  getActions: () => ({
    geometric: null,
    partDescriptor: {},
  }),
};

const AnnotationFlyoutActionContext =
  createContext<AnnotationFlyoutActionContextType>(DEFAULT_VALUE);

type AnnotationFlyoutActionProviderProps = {
  children: React.ReactNode;
  getActions: GetAnnotationFlyoutActions;
};

export const AnnotationFlyoutActionProvider = ({
  children,
  getActions,
}: AnnotationFlyoutActionProviderProps) => {
  const contextValue = useMemo(() => ({ getActions }), [getActions]);

  return (
    <AnnotationFlyoutActionContext.Provider value={contextValue}>
      {children}
    </AnnotationFlyoutActionContext.Provider>
  );
};

export const useAnnotationFlyoutActions =
  (): AnnotationFlyoutActionContextType => {
    const context = useContext(AnnotationFlyoutActionContext);
    // Return empty object if used outside provider instead of throwing error
    return context ?? DEFAULT_VALUE;
  };
